import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

interface MatchDayToggleProps {
  onStatusChange?: (enabled: boolean) => void;
}

const MatchDayToggle = ({ onStatusChange }: MatchDayToggleProps) => {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchMatchDayStatus();
  }, []);

  const fetchMatchDayStatus = async () => {
    try {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "match_day_mode")
        .maybeSingle();

      if (error) throw error;

      if (data?.value) {
        const value = data.value as { enabled: boolean };
        setEnabled(value.enabled ?? true);
      }
    } catch (error) {
      console.error("Error fetching match day status:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMatchDayMode = async () => {
    setUpdating(true);
    const newValue = !enabled;

    try {
      const { error } = await supabase
        .from("site_settings")
        .update({ 
          value: { enabled: newValue },
          updated_at: new Date().toISOString()
        })
        .eq("key", "match_day_mode");

      if (error) throw error;

      setEnabled(newValue);
      onStatusChange?.(newValue);
      
      if (newValue) {
        toast.success("Mode Jour de Match activé - Les tests vont dans le Dashboard");
      } else {
        toast.warning("Mode Jour de Match désactivé - Email auto 'Victime de notre succès' + promo 30%");
      }
    } catch (error) {
      console.error("Error updating match day mode:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Chargement...</span>
      </div>
    );
  }

  return (
    <div 
      className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${
        enabled 
          ? "bg-green-500/10 border-green-500/30" 
          : "bg-amber-500/10 border-amber-500/30"
      }`}
    >
      <div className="flex items-center gap-2">
        <Zap className={`w-4 h-4 ${enabled ? "text-green-400" : "text-amber-400"}`} />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">
            Mode Jour de Match
          </span>
          <span className="text-xs text-muted-foreground">
            {enabled ? "Tests → Dashboard" : "Tests → Email promo 30%"}
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {!enabled && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-400 animate-pulse">
            PAUSE
          </Badge>
        )}
        <Switch
          checked={enabled}
          onCheckedChange={toggleMatchDayMode}
          disabled={updating}
          className={enabled ? "data-[state=checked]:bg-green-500" : ""}
        />
      </div>
    </div>
  );
};

export default MatchDayToggle;
